import { SeatingSelectReturnType } from "@shared/modules/seating/types/seating-types";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import { useMemo, useState } from "react";
import ItemSelectModalSelectCustomerScreen from "@app-contexts/dashboard/features/box-office/seating/components/ItemSelectModal/ItemSelectModalSelectCustomerScreen.web";
import {
  DefaultLocationSelectModalProps,
  MapItemType,
} from "./DefaultLocationSelect.props";
import DefaultLocationSelect from "./DefaultLocationSelect/DefaultLocationSelect.web";
import { useDefaultLocationSelectModal } from "./useDefaultLocationSelectModal";

const DefaultLocationSelectModal = (props: DefaultLocationSelectModalProps) => {
  const { t } = useTranslation("seating");
  const { itemsOnSeat, resolve, isOpen } = props;
  const headerText = useMemo(
    () => `${t("Location")} ${itemsOnSeat?.$data?.identifier}`,
    [itemsOnSeat, t]
  );

  const [locationSelections, setLocationSelections] = useState<
    Array<SeatingSelectReturnType>
  >([]);
  const updateLocationSelections = (input: Array<SeatingSelectReturnType>) => {
    setLocationSelections(input);
  };

  const {
    locationItems,
    selectItemPermission,
    getCurrentSelectedAmount,
    cancelModal,
    screen,
    handleScreen,
    customerSelected,
    handleSelectCustomer,
    handleUnselectCustomer,
    areAllRenewalItemGroups,
    seatingEvent,
    itemGroups,
    handleDoneClick,
  } = useDefaultLocationSelectModal({
    ...props,
    locationSelections,
    updateLocationSelections,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={cancelModal}
      size="3xl"
      isCentered
      variant="interactive"
      scrollBehavior="inside"
    >
      <Modal.Header alignItems="center">{headerText}</Modal.Header>
      <Modal.Body>
        {screen === "select-item" && (
          <Flex
            data-testid="default-location-select-modal"
            w="full"
            direction="column"
            gap="md"
            mt="none"
          >
            {locationItems.map((item: MapItemType) => (
              <DefaultLocationSelect
                key={item.id}
                item={item}
                selectItemPermission={selectItemPermission}
                getCurrentSelectedAmount={getCurrentSelectedAmount}
                handleScreen={handleScreen}
                {...props}
              />
            ))}
          </Flex>
        )}
        {seatingEvent && screen === "select-customer" && (
          <ItemSelectModalSelectCustomerScreen
            seatingEvent={seatingEvent}
            itemGroups={itemGroups}
            customerSelected={customerSelected}
            handleSelectCustomer={handleSelectCustomer}
            handleUnselectCustomer={handleUnselectCustomer}
          />
        )}
      </Modal.Body>
      {!areAllRenewalItemGroups && (
        <Modal.Footer
          borderTop="1px solid"
          borderColor="gray.400"
          flexDirection="row"
          justifyContent="flex-end"
        >
          <Button.Ghost
            data-testid="location-select-modal-continue"
            minWidth="element.7xl"
            marginRight="auto"
            onClick={cancelModal}
          >
            {t("Cancel", { ns: "common" })}
          </Button.Ghost>
          <Button
            data-testid="default-location-select-modal-continue"
            minWidth="element.7xl"
            variant="solid"
            colorPalette="primary"
            isDisabled={!locationSelections.length}
            onClick={() => resolve(locationSelections)}
          >
            {t("Continue", { ns: "common" })}
          </Button>
        </Modal.Footer>
      )}
      {areAllRenewalItemGroups && screen === "select-customer" && (
        <Modal.Footer>
          <Flex justifyContent="flex-end">
            <Button.Solid
              isDisabled={!customerSelected}
              data-testid="item-select-modal-select-customer-screen-done-button"
              onClick={handleDoneClick}
            >
              {t("Done", { ns: "common" })}
            </Button.Solid>
          </Flex>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default DefaultLocationSelectModal;
